import { AchievementTier, LadderRungView, LadderView, TierPipView } from './achievement.model';
import { GamificationAchievement, GamificationDay } from './gamification-bootstrap.model';

export interface GamificationWeekDayView {
  key: string;
  label: string;
  completed: boolean;
  state: 'complete' | 'pending';
  statusLabel: string;
}

const TIER_LABELS: { [tier: string]: string } = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
  diamond: 'Diamond'
};

export function mapAchievementToLadder(achievement: GamificationAchievement): LadderView {
  const tiers = (achievement.Tiers || []).slice().sort((left, right) => left.TierOrder - right.TierOrder);
  const earnedTiers = tiers.filter(tier => tier.Earned);
  const currentTier = earnedTiers.length ? normalizeTier(earnedTiers[earnedTiers.length - 1].TierKey) : null;
  const statusLabel = achievement.Mastered
    ? 'Mastered'
    : currentTier
      ? `${TIER_LABELS[currentTier]} · Tier ${earnedTiers.length} of ${tiers.length}`
      : 'No tier yet';

  return {
    key: `${achievement.AchievementKey}-${achievement.StudentCourseId || 'global'}`,
    title: achievement.Name,
    description: achievement.Description,
    iconCssClass: normalizeIconCssClass(achievement.IconCssClass),
    tier: achievement.Mastered ? normalizeTier(tiers[tiers.length - 1]?.TierKey) : currentTier,
    statusLabel,
    percent: clampPercent(achievement.ProgressPercent),
    percentLabel: `${Math.round(clampPercent(achievement.ProgressPercent))}%`,
    mastered: achievement.Mastered,
    footnote: achievement.AchievementKey === 'study-streak'
      ? 'Weather days protect a missed day automatically when one is available.'
      : undefined,
    pips: tiers.map(tier => ({
      key: `${achievement.AchievementKey}-${tier.AchievementTierId}`,
      tier: normalizeTier(tier.TierKey),
      state: tier.Earned ? 'earned' : 'locked'
    } as TierPipView)),
    rungs: tiers.map((tier, index) => ({
      key: `${achievement.AchievementKey}-rung-${tier.AchievementTierId}`,
      tier: normalizeTier(tier.TierKey),
      tierLabel: TIER_LABELS[normalizeTier(tier.TierKey)],
      requirement: tier.RequirementText || `${tier.Threshold}`,
      rungNumber: index + 1,
      state: tier.Earned ? 'earned' : tier.AchievementTierId === achievement.NextAchievementTierId ? 'current' : 'locked',
      earnedLabel: tier.Earned && tier.EarnedDateTime ? `Earned ${formatDate(tier.EarnedDateTime)}` : undefined,
      progressLabel: tier.AchievementTierId === achievement.NextAchievementTierId
        ? `${formatMetric(achievement.CurrentMetricValue)} of ${formatMetric(tier.Threshold)}`
        : undefined,
      percent: tier.AchievementTierId === achievement.NextAchievementTierId
        ? clampPercent(achievement.ProgressPercent)
        : 0
    } as LadderRungView))
  };
}

export function mapGamificationWeek(days: GamificationDay[], today: Date = new Date(), weekStartDay: number = 0): GamificationWeekDayView[] {
  const labels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const byDate = new Map<string, GamificationDay>();
  (days || []).forEach(day => byDate.set(activityDateKey(day.ActivityDate), day));
  const normalizedWeekStart = Math.max(0, Math.min(6, weekStartDay || 0));
  const daysSinceWeekStart = (today.getDay() - normalizedWeekStart + 7) % 7;
  const weekStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysSinceWeekStart);

  return labels.map((unused, index) => {
    const date = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + index);
    const day = byDate.get(dateKey(date));
    const completed = !!day?.HasActualActivity || !!day?.WeatherDayApplied;
    const isToday = dateKey(date) === dateKey(today);
    const weekday = date.toLocaleDateString(undefined, { weekday: 'long' });

    return {
      key: dateKey(date),
      label: labels[date.getDay()],
      completed,
      state: completed ? 'complete' : 'pending',
      statusLabel: `${weekday}${isToday ? ', today' : ''} — ${completed ? day?.WeatherDayApplied ? 'weather day applied' : 'completed' : isToday || date < today ? 'not completed' : 'upcoming'}`
    };
  });
}

function normalizeTier(value: string): AchievementTier {
  const tier = (value || '').toLowerCase() as AchievementTier;
  return TIER_LABELS[tier] ? tier : 'bronze';
}

function normalizeIconCssClass(value: string): string {
  const iconCssClass = (value || 'fa fa-trophy')
    .replace(/\bfa-solid\b/g, 'fa')
    .replace(/\bfa-plane-arrival\b/g, 'fa-plane');

  return iconCssClass.indexOf('fa ') === 0 || iconCssClass.indexOf(' fa ') >= 0
    ? iconCssClass
    : `fa ${iconCssClass}`;
}

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value || 0));
}

function formatMetric(value: number): string {
  return `${Math.round(Number(value || 0))}`;
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function dateKey(value: Date): string {
  return `${value.getFullYear()}-${padDatePart(value.getMonth() + 1)}-${padDatePart(value.getDate())}`;
}

function activityDateKey(value: string): string {
  const calendarDate = (value || '').substring(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(calendarDate)
    ? calendarDate
    : dateKey(new Date(value));
}

function padDatePart(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}
